import { useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { DataContext } from "../context/DataContext";
import LoadingScreen from "../utilities/LoadingScreen";
import Select from "react-select";
import { FaChevronDown, FaChevronUp, FaFilter } from "react-icons/fa";

const apiDomain = process.env.REACT_APP_API_DOMAIN; // Assuming you're using this for API domain

const Wallet = () => {
  const [selectedUser, setSelectedUser] = useState(null); // Set initial user
  const [participants, setParticipants] = useState(null);
  const [balance, setBalance] = useState(0); // Current user balance
  const [transactions, setTransactions] = useState([]); // User transaction history
  const [filters, setFilters] = useState({
    event_name: "",
  });
  const [availableFilters, setAvailableFilters] = useState({});
  const [filteredParticipants, setFilteredParticipants] = useState([]);
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(true);
  const { usersInfo, participant, loading, allEventsSummary } = useContext(DataContext);

  // Generate filter options based on available events
  const generateFilterOptions = useCallback((userList) => {
    if (!allEventsSummary || !Array.isArray(allEventsSummary)) return;

    // Create event options
    const eventOptions = allEventsSummary.map(event => ({
      value: event.event_name,
      label: `${event.event_name}`
    }));

    const options = {
      event_name: eventOptions,
    };

    setAvailableFilters(options);
  }, [allEventsSummary]);

  // console.log('participants',participant )

  useEffect(() => {
    let userList = null;

    if (usersInfo && Array.isArray(usersInfo)) {
      userList = usersInfo;
    } else if (usersInfo && usersInfo.users) {
      userList = usersInfo.users;
    } else if (usersInfo && usersInfo.data) {
      userList = usersInfo.data;
    }

    if (userList && userList.length > 0) {
      // Merge participant numeric IDs into user objects
      const participantArray = Array.isArray(participant) ? participant : [];
      console.log("📊 USERS from userinformation:", userList.length);
      console.log("👥 PARTICIPANTS from participants data:", participantArray.length);
      
      // Create a map of participant user IDs by numeric ID (userId or facebookId)
      const participantMap = {};
      participantArray.forEach(p => {
        // Map by userId or facebookId - these are the numeric IDs
        const id = p.userId || p.facebookId;
        if (id) {
          participantMap[id] = id; // Direct mapping: numeric ID -> numeric ID
        }
      });
      
      console.log("📌 Participant ID Map:", participantMap);
      
      // Merge participant numeric IDs into user objects
      const enrichedUserList = userList.map(user => {
        // Try to match by extracting numeric ID from user.id or user.user_id
        // Users from userinformation might have facebook: prefix or just numeric ID
        const userNumericId = user.id || user.user_id || user.userId;
        const cleanNumericId = userNumericId ? String(userNumericId).replace('facebook:', '') : null;
        
        // Check if this user exists in participants
        const participantUserId = participantMap[cleanNumericId];
        
        return {
          ...user,
          numericUserId: participantUserId || cleanNumericId, // Use matched ID or clean ID as fallback
        };
      });
      
      console.log("📌 Enriched users with numeric IDs:", enrichedUserList);
      
      setParticipants(enrichedUserList);
      setFilteredParticipants(enrichedUserList);
      setSelectedUser(enrichedUserList[0]);
      generateFilterOptions(enrichedUserList);
    } else {
      setParticipants([]);
      setFilteredParticipants([]);
    }
  }, [usersInfo, participant, allEventsSummary, generateFilterOptions]);

  // Apply filters when filters or participants change
  useEffect(() => {
    if (!participants || participants.length === 0) {
      setFilteredParticipants([]);
      return;
    }

    let filtered = [...participants];

    if (filters.event_name) {
      // Find the event object from allEventsSummary based on event name
      const eventObj = allEventsSummary.find(event => event.event_name === filters.event_name);
      const eventId = eventObj?.event_id;

      if (eventId) {
        // Filter participants based on participationDetails containing the event ID
        filtered = participants.filter((user) =>
          user.participationDetails?.some(
            (detail) => detail.event_id === eventId
          )
        );
      }
    }

    setFilteredParticipants(filtered);
  }, [filters, participants, allEventsSummary]);

  // Function to fetch wallet balance and transactions for a selected user
  const fetchWalletDetails = async (userId) => {
    try {
      if (!userId) return;

      console.log("🔍 Fetching wallet for userId:", userId, "Type:", typeof userId);

      // Fetch wallet balance
      const balanceResponse = await axios.get(`${apiDomain}/api/v2/getwallet`, {
        params: { user_id: userId },
      });
      
      console.log("💰 Wallet API Response:", balanceResponse.data);
      setBalance(balanceResponse.data.amount || 0);

      // Fetch transaction history
      const transactionResponse = await axios.get(
        `${apiDomain}/api/v2/filteredtransactions`,
        {
          params: { user_id: userId },
        }
      );
      console.log("📜 Transactions:", transactionResponse.data);
      setTransactions(transactionResponse.data ? transactionResponse.data : []);
    } catch (error) {
      console.error("❌ Wallet fetch error:", error.message, error.response?.data);
      setBalance(0);
      setTransactions([]);
    }
  };

  // Handle adding funds
  const handleAddFunds = async () => {
    const amount = prompt("Enter the amount to add:");
    if (amount && parseFloat(amount) > 0) {
      try {
        const userId = selectedUser.numericUserId;
        const response = await axios.post(
          `${apiDomain}/api/v2/wallet/add`,
          null,
          {
            params: { user_id: userId, amount: amount },
          }
        );
        setBalance(response.data.new_balance);
        await addTransaction("credit", amount);
        alert("Funds added successfully!");
      } catch (error) {
        alert("Failed to add funds");
      }
    }
  };

  // Handle deducting funds
  const handleDeductFunds = async () => {
    const amount = prompt("Enter the amount to deduct:");
    if (amount && parseFloat(amount) > 0) {
      try {
        const userId = selectedUser.numericUserId;
        const response = await axios.delete(
          `${apiDomain}/api/v2/wallet/deduct`,
          {
            params: { user_id: userId, amount: amount },
          }
        );
        setBalance(response.data.new_balance);
        await addTransaction("debit", -amount);
        alert("Funds deducted successfully!");
      } catch (error) {
        alert("Failed to deduct funds");
      }
    }
  };

  // Function to record a transaction (Add or Deduct)
  const addTransaction = async (type, amount) => {
    const userId = selectedUser.numericUserId;
    const transaction = {
      transaction_id: `TXN-${Date.now()}`,
      timestamp: new Date(),
      type: type,
      amount: parseFloat(amount),
      status: "success",
      user_id: parseInt(userId),
    };

    try {
      await axios.post(`${apiDomain}/api/v2/transaction/create`, transaction);
      setTransactions((prev) => [transaction, ...prev]);
    } catch (error) {
      alert("Failed to record transaction");
    }
  };

  useEffect(() => {
    if (selectedUser) {
      // Use the numeric user ID from participant mapping
      const userId = selectedUser.numericUserId;
      console.log("👤 Selected User:", selectedUser.name);
      console.log("🔑 Using numeric userId for wallet:", userId);
      
      if (userId) {
        fetchWalletDetails(userId);
      } else {
        console.warn("⚠️ No numeric user ID found for user:", selectedUser.name);
      }
    }
  }, [selectedUser]);

  // Handle filter changes
  const handleFilterChange = (selectedOption, actionMeta) => {
    const { name } = actionMeta;
    const value = selectedOption ? selectedOption.value : "";
    setFilters({ ...filters, [name]: value });
  };

  // Clear all filters
  const clearAllFilters = () => {
    setFilters({
      event_name: "",
    });
  };

  if (loading) return <LoadingScreen />;

  return (
    <div className="lg:mr-[2vw] mx-2 my-10">
      {/* Filter Panel - moved outside main card */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm mb-6 overflow-hidden">
        <div
          className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
          onClick={() => setIsFiltersExpanded(!isFiltersExpanded)}
        >
          <div className="flex items-center gap-3">
            <FaFilter className="text-gray-600" />
            <h3 className="text-lg font-semibold text-gray-800">Filters</h3>
            <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
              {Object.values(filters).filter(v => v !== "").length} active
            </span>
          </div>
          <div className="flex items-center gap-2">
            {Object.values(filters).some(v => v !== "") && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  clearAllFilters();
                }}
                className="text-sm text-red-600 hover:text-red-800 font-medium"
              >
                Clear All
              </button>
            )}
            {isFiltersExpanded ? (
              <FaChevronUp className="text-gray-600" />
            ) : (
              <FaChevronDown className="text-gray-600" />
            )}
          </div>
        </div>

        {isFiltersExpanded && (
          <div className="px-4 pb-4 border-t border-gray-100">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
              {Object.keys(filters).map((key) => {
                const placeholder = `Select ${key.replace("_", " ").replace(/\b\w/g, l => l.toUpperCase())}`;
                const currentValue = availableFilters[key]?.find(option => option.value === filters[key]);

                return (
                  <div key={key} className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700 capitalize">
                      {key.replace("_", " ")}
                    </label>
                    <Select
                      name={key}
                      value={currentValue || null}
                      onChange={handleFilterChange}
                      options={availableFilters[key] || []}
                      placeholder={placeholder}
                      isClearable
                      className="react-select-container"
                      classNamePrefix="react-select"
                      menuPortalTarget={document.body}
                      styles={{
                        control: (provided, state) => ({
                          ...provided,
                          minHeight: '44px',
                          borderColor: state.isFocused ? '#3B82F6' : '#D1D5DB',
                          boxShadow: state.isFocused ? '0 0 0 1px #3B82F6' : 'none',
                          '&:hover': {
                            borderColor: '#9CA3AF',
                          },
                          borderRadius: '8px',
                          fontSize: '14px',
                        }),
                        option: (provided, state) => ({
                          ...provided,
                          backgroundColor: state.isSelected ? '#3B82F6' : state.isFocused ? '#EBF4FF' : 'white',
                          color: state.isSelected ? 'white' : '#374151',
                          padding: '10px 12px',
                          fontSize: '14px',
                          cursor: 'pointer',
                        }),
                        singleValue: (provided) => ({
                          ...provided,
                          color: '#374151',
                          fontSize: '14px',
                        }),
                        placeholder: (provided) => ({
                          ...provided,
                          color: '#9CA3AF',
                          fontSize: '14px',
                        }),
                        menu: (provided) => ({
                          ...provided,
                          borderRadius: '8px',
                          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
                          border: '1px solid #E5E7EB',
                          zIndex: 9999,
                        }),
                        menuList: (provided) => ({
                          ...provided,
                          padding: '4px',
                          maxHeight: '200px',
                        }),
                        menuPortal: (provided) => ({
                          ...provided,
                          zIndex: 9999,
                        }),
                      }}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Main Wallet Card with Left-Right Layout */}
      <div className="flex flex-col md:flex-row bg-white rounded-lg shadow-lg border border-gray-300">
        {/* Left Side: User List */}
        <div className="md:w-1/3 w-full p-6 bg-gray-100 border-r border-gray-300">
          <h2 className="text-xl font-bold mb-4">Users</h2>
          <ul className="space-y-2">
          {filteredParticipants &&
            filteredParticipants.map((user, idx) => {
              const displayName = user.instagramUser?.name || user.name || user.username || user.full_name || user.displayName || 'Unknown User';
              const userId = user.userId || user.id || user.user_id;
              if (idx === 0) console.log("📌 First user in list:", { displayName, userId, userKeys: Object.keys(user).slice(0, 5) });
              return (
                <li
                  key={user.id || user.userId || idx}
                  className={`p-3 cursor-pointer rounded-lg ${
                    selectedUser && (selectedUser.id === user.id || selectedUser.userId === user.userId)
                      ? "bg-blue-200"
                      : "bg-gray-200"
                  } hover:bg-blue-100`}
                  onClick={() => setSelectedUser(user)}
                  title={`ID: ${userId}`}
                >
                  {displayName}
                </li>
              );
            })}
          </ul>
        </div>

        {/* Right Side: Wallet Details */}
        <div className="md:w-2/3 w-full p-6">
          {selectedUser && (
            <div>
              <h2 className="text-2xl font-bold text-center mb-6">
                {(selectedUser.instagramUser && selectedUser.instagramUser.name) || selectedUser.name || selectedUser.username || selectedUser.full_name || selectedUser.displayName || 'Unknown User'}'s Wallet
              </h2>

              <div className="text-center mb-6 bg-gradient-to-r from-green-50 to-blue-50 p-6 rounded-lg border-2 border-green-200">
                <h3 className="text-lg font-semibold text-gray-700 mb-2">Current Balance</h3>
                <p className="text-4xl font-bold text-green-600">₹{balance.toFixed(2)}</p>
              </div>

              <div className="flex gap-3 justify-center mb-6">
                <button
                  onClick={handleAddFunds}
                  className="py-2 px-6 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition font-semibold"
                >
                  ➕ Add Funds
                </button>
                <button
                  onClick={handleDeductFunds}
                  className="py-2 px-6 bg-red-500 text-white rounded-lg hover:bg-red-600 transition font-semibold"
                >
                  ➖ Deduct Funds
                </button>
              </div>

              <hr className="my-6" />

              <h3 className="text-xl font-semibold mb-4">Transaction History</h3>
              <div className="overflow-auto max-h-64">
                {transactions && transactions.length > 0 ? (
                  <ul className="space-y-2">
                    {transactions.map((transaction) => (
                      <li
                        key={transaction.id}
                        className={`flex justify-between p-3 rounded-lg ${
                          transaction.amount < 0 ? "bg-red-100" : "bg-green-100"
                        }`}
                      >
                        <div>
                          <p className="font-medium capitalize">{transaction.type}</p>
                          <p className="text-sm text-gray-600">
                            {new Date(transaction.timestamp).toLocaleDateString()}
                          </p>
                        </div>
                        <p
                          className={`font-bold ${
                            transaction.amount < 0
                              ? "text-red-600"
                              : "text-green-600"
                          }`}
                        >
                          {transaction.amount < 0 ? "-" : "+"}₹
                          {Math.abs(transaction.amount).toFixed(2)}
                        </p>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-gray-500 text-center py-6">
                    No transactions available.
                  </p>
                )}
              </div>
            </div>
          )}
          {!selectedUser && (
            <p className="text-center text-gray-500 py-8">
              No user selected. Please select a user from the list or make sure participants data is loaded.
            </p>
          )}
      </div>
    </div>
  </div>
  );
};

export default Wallet;
